// App.js
import React, { useState, useEffect, useRef, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import PageLoader from "./components/shared/PageLoader";
import lazyWithRetry from "./utils/lazyWithRetry";

// Context Providers
import { WebRTCProvider } from "./context/WebRTCContext";
import { FinanceNotificationProvider } from "./context/FinanceNotificationContext";
import { IncomingCallProvider } from "./context/IncomingCallContext";
import PersistentChatsHost from "./components/calling/PersistentChatsHost";
import { authService, isDefinitiveAuthError, isAuthStorageKey } from "./utils/auth";
import { Toaster } from "./components/ui/sonner";
import RouteErrorBoundary from "./components/RouteErrorBoundary";

// Styling
import "./App.css";
import "./styles/design-system.css";

// Feature Route Modules (Lazy-loaded via separate entry points)
import PublicRoutes from "./routes/PublicRoutes";
import WorkspaceRoutes from "./routes/WorkspaceRoutes";
const CommerceRoutes = lazyWithRetry(() => import("./routes/CommerceRoutes"));
const FinanceRoutes = lazyWithRetry(() => import("./routes/FinanceRoutes"));
const OpsRoutes = lazyWithRetry(() => import("./routes/OpsRoutes"));
const AdminRoutes = lazyWithRetry(() => import("./routes/AdminRoutes"));
const InternalRoutes = lazyWithRetry(() => import("./routes/InternalRoutes"));
const SettingsRoutes = lazyWithRetry(() => import("./routes/SettingsRoutes"));

// Procurement Module — mounted inside WorkspaceLayoutNew (same pattern as Commerce/Finance)
const ProcurementRoutes = lazyWithRetry(() => import("./pages/procurement/routes/ProcurementRoutes"));

// Workforce Module — mounted inside WorkspaceLayoutNew (same pattern as Procurement)
const WorkforceRoutes = lazyWithRetry(() => import("./pages/workforce/routes/WorkforceRoutes"));

// Client Portal Integration Imports
const ClientPortalRouter = lazyWithRetry(() => import("./client-portal/ClientPortalRouter"));
import { AuthProvider as ClientPortalAuthProvider } from "./client-portal/context/AuthContext";
import { ProfileProvider as ClientPortalProfileProvider } from "./client-portal/context/ProfileContext";

// Vendor Portal Integration Imports
const VendorPortalRouter = lazyWithRetry(() => import("./vendor-portal/VendorPortalRouter"));
import { VendorAuthProvider } from "./vendor-portal/context/VendorAuthContext";

// Core Infrastructure Pages (Keep in main bundle for smooth entry)
import AcceptInvite from "./pages/AcceptInvite";
import SynexosHome from "./pages/SynexosHome";
import { SessionProvider } from "./context/SessionContext";
import { usePermissions } from "./hooks/usePermissions";
import { firstAvailableRoute } from "./config/solutions";
import { logRouteDiagnostic, logAuthDiagnostic } from "./utils/blankPageDiagnostics";
import { logger } from "./utils/logger";

// Heavy Layout & Auth Pages (Lazy loaded for performance)
const LoginPage = lazyWithRetry(() => import("./pages/auth/LoginPage"));
const SignupPage = lazyWithRetry(() => import("./pages/auth/SignupPage"));
const ForgotPasswordPage = lazyWithRetry(() => import("./pages/auth/ForgotPasswordPage"));
const WorkspaceLayoutNew = lazyWithRetry(() => import("./pages/workspace/WorkspaceLayoutNew"));
const ProposalRespondPage = lazyWithRetry(() => import("./pages/commerce/revenue-workflow/ProposalRespondPage"));
const WaitingForActivation = lazyWithRetry(() => import("./pages/auth/WaitingForActivation"));
const MyProfilePage = lazyWithRetry(() => import("./pages/profile/MyProfilePage"));

// Synex Connect — standalone meeting experience (full-screen, no Workspace chrome).
const ConnectRoutes = lazyWithRetry(() => import("./modules/connect"));

// Synex Meet — PUBLIC guest meeting route (/meet/:code), full-screen, NO auth.
// Separate lazy chunk so the guest landing never bundles MeetingShell / LiveKit.
const MeetRoutes = lazyWithRetry(() => import("./modules/connect/routes/MeetRoutes"));

// Specialized Top-level modules
const EmailCampaigns = lazyWithRetry(() => import("./pages/workspace/email-campaigns/EmailCampaigns"));
const WorkflowBuilder = lazyWithRetry(() => import("./pages/workspace/workflows/WorkflowBuilder"));
const PublicLeadCaptureLayout = lazyWithRetry(() => import("./pages/commerce/revenue-workflow/PublicLeadCaptureLayout"));
const PublicCareersPage = lazyWithRetry(() => import("./pages/workforce/pages/PublicCareersPage"));
const PublicJobDetailPage = lazyWithRetry(() => import("./pages/workforce/pages/PublicJobDetailPage"));

// Synex Mail — Public Landing Page (Google OAuth Application Home Page)
const MailLandingPage = lazyWithRetry(() => import("./pages/MailLandingPage"));

// Synex Mail — Public Legal Pages (no auth required)
const MailPrivacyPolicy = lazyWithRetry(() => import("./pages/mail-legal/MailPrivacyPolicy"));
const MailTermsOfService = lazyWithRetry(() => import("./pages/mail-legal/MailTermsOfService"));
const MailDataDeletion = lazyWithRetry(() => import("./pages/mail-legal/MailDataDeletion"));
const MailSecurity = lazyWithRetry(() => import("./pages/mail-legal/MailSecurity"));

import { subscribeAuthSync, AUTH_SYNC_EVENTS } from "./utils/authSyncChannel";

/**
 * @param solution  Optional solution key. When present the section is only reachable
 *                  by a member holding that solution. This is UX only: the backend
 *                  gate (require_solution) is the enforcement boundary, and manual URL
 *                  entry is rejected there regardless of what this component does.
 */
function PrivateRoute({ children, solution }) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshAttempted, setRefreshAttempted] = useState(false);
  const [authDefinitiveFailed, setAuthDefinitiveFailed] = useState(false);
  const [isTransientError, setIsTransientError] = useState(false);
  const isMountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const { hasSolution } = usePermissions();
  const location = useLocation();

  const isAuth = authService.isAuthenticated();
  const hasRefresh = authService.hasRefreshToken();
  const hasUserSession = Boolean(authService.getUser());

  // If unauthenticated but a refresh capability exists, only resolve auth if an active user session is present
  const isResolvingAuth = !isAuth && hasRefresh && hasUserSession && !refreshAttempted && !authDefinitiveFailed && !isTransientError;

  useEffect(() => {
    isMountedRef.current = true;
    const handleOnline = () => {
      if (isMountedRef.current) {
        setIsTransientError(false);
        setRefreshAttempted(false);
      }
    };
    window.addEventListener("online", handleOnline);
    return () => {
      isMountedRef.current = false;
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  useEffect(() => {
    if (!isAuth && hasRefresh && hasUserSession && !refreshAttempted && !authDefinitiveFailed && !isTransientError && !inFlightRef.current) {
      inFlightRef.current = true;
      logAuthDiagnostic("PrivateRoute.silent_refresh_started");
      setIsRefreshing(true);

      authService.refreshToken()
        .then(() => {
          logAuthDiagnostic("PrivateRoute.silent_refresh_succeeded");
          if (isMountedRef.current) {
            setIsTransientError(false);
          }
        })
        .catch((err) => {
          logger.warn("[PrivateRoute] Silent refresh failed:", err?.message || "Silent refresh error");
          logAuthDiagnostic("PrivateRoute.silent_refresh_failed", { error: err?.message });
          if (isDefinitiveAuthError(err)) {
            if (isMountedRef.current) {
              setAuthDefinitiveFailed(true);
            }
          } else {
            if (isMountedRef.current) {
              setIsTransientError(true);
            }
          }
        })
        .finally(() => {
          inFlightRef.current = false;
          if (isMountedRef.current) {
            setRefreshAttempted(true);
            setIsRefreshing(false);
          }
        });
    }
  }, [isAuth, hasRefresh, hasUserSession, refreshAttempted, authDefinitiveFailed, isTransientError]);

  // Show loading while executing silent refresh or resolving initial unauth state
  if (isRefreshing || isResolvingAuth) {
    return <PageLoader />;
  }

  // If unauthenticated due to transient connection interruption, show offline/retry state without exposing protected content or prematurely kicking user out to login
  if (!isAuth && isTransientError && !authDefinitiveFailed) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-2xl flex items-center justify-center mb-4 shadow-sm">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.828m2.829 2.828L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.828m-4.243 4.243a9 9 0 01-12.728 0m0 0l2.828-2.829m-2.828 2.829L3 21m2.828-5.657a5 5 0 010-7.072m0 0l2.829 2.829m4.242-4.242a1 1 0 00-1.414 0L3 14" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Connection Interrupted</h2>
        <p className="text-sm text-gray-600 max-w-md mb-6">
          Unable to verify your session due to a network connection issue. Your session is preserved and will reconnect once online.
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setRefreshAttempted(false);
              setIsTransientError(false);
            }}
            className="px-5 py-2.5 bg-[#033F99] hover:bg-[#022861] text-white text-sm font-medium rounded-xl transition-all shadow-md"
          >
            Retry Verification
          </button>
          <a
            href="/auth/login"
            className="px-5 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-xl transition-all"
          >
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  // Redirect to login only when unauthenticated AND (no refresh capability OR definitive 401 auth error OR no cached user session OR refresh attempt completed)
  if (!isAuth && (!hasRefresh || authDefinitiveFailed || !hasUserSession || refreshAttempted)) {
    logRouteDiagnostic("PrivateRoute_redirect_to_login", {
      isAuth,
      hasRefresh,
      refreshAttempted,
      authDefinitiveFailed,
      hasUserSession,
    });
    // Remember the requested deep link (validated again by LoginPage before use) so a link
    // opened in a new, logged-out tab returns there after sign-in.
    return (
      <Navigate
        to="/auth/login"
        replace
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  // Check organization activation status
  const orgStatus = localStorage.getItem('org_status');

  if (orgStatus === 'inactive') {
    logRouteDiagnostic("PrivateRoute_redirect_to_waiting_for_activation");
    return <Navigate to="/auth/waiting-for-activation" replace />;
  }

  // Solution Access gate. Redirect to the first solution the user DOES hold
  if (solution && !hasSolution(solution)) {
    const fallback = firstAvailableRoute(hasSolution);
    logRouteDiagnostic("PrivateRoute_solution_denied_redirecting", {
      solution,
      fallback,
    });
    logger.warn(`[Route] Solution "${solution}" not granted. Redirecting to ${fallback}.`);
    return <Navigate to={fallback} replace />;
  }

  return children;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => authService.isAuthenticated());
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(() => authService.getUser());

  useEffect(() => {
    let isMounted = true;

    const syncAuthState = () => {
      if (!isMounted) return;
      const authStatus = authService.isAuthenticated();
      setIsAuthenticated(authStatus);

      if (authStatus) {
        const userData = authService.getUser();
        setCurrentUser(userData);
      } else {
        setCurrentUser(null);
      }
    };

    // Check authentication status on mount
    const checkAuth = async () => {
      try {
        syncAuthState();
      } catch (globalErr) {
        logger.error("[App] Unexpected error during checkAuth:", globalErr?.message || "Unexpected checkAuth error");
        if (isMounted) {
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      } finally {
        if (isMounted) {
          setAuthLoading(false);
        }
      }
    };
    checkAuth();

    // Subscribe to in-memory and cross-tab auth sync events
    const unsubscribeAuthSync = subscribeAuthSync((event) => {
      if (!isMounted) return;
      if (event.type === AUTH_SYNC_EVENTS.AUTH_LOGIN || event.type === AUTH_SYNC_EVENTS.AUTH_TOKEN_UPDATED) {
        syncAuthState();
      } else if (event.type === AUTH_SYNC_EVENTS.AUTH_LOGOUT) {
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
    });

    // Also listen for storage changes (e.g., when user metadata is updated in another tab)
    const handleStorageChange = (e) => {
      // Ignore unrelated keys (activity heartbeat, meeting leases, app state) so that
      // background tabs do not re-render the whole app on every write from another tab.
      if (e && !isAuthStorageKey(e.key)) return;
      syncAuthState();
    };

    // BFCache restoration handler: enforce immediate auth verification on browser Back/Forward
    const handlePageShow = (event) => {
      if (event.persisted) {
        const isAuth = authService.isAuthenticated();
        const hasUser = Boolean(authService.getUser());
        const pathname = typeof window !== "undefined" ? window.location.pathname : "";
        const segments = pathname.split("/").filter(Boolean);
        const reservedTopLevel = ["workspace", "commerce", "finance", "operations", "solutions", "admin", "internal", "home", "email-campaigns", "workflows", "dashboard"];
        const isOrgCustomSlugPath = segments.length === 2 && !reservedTopLevel.includes(segments[0]);

        const isPublicPath =
          pathname === "/" ||
          pathname.startsWith("/auth/") ||
          pathname.startsWith("/proposal/") ||
          pathname.startsWith("/accept-invite") ||
          pathname.startsWith("/capture/lead") ||
          pathname.startsWith("/meet/") ||
          pathname.startsWith("/mail") ||
          pathname.startsWith("/offer/review") ||
          isOrgCustomSlugPath;

        if (!isAuth && (!hasUser || !authService.hasRefreshToken()) && !isPublicPath) {
          window.location.replace("/auth/login");
        } else {
          syncAuthState();
        }
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      isMounted = false;
      unsubscribeAuthSync();
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  if (authLoading) {
    return <PageLoader />;
  }

  return (
    <div className="App">
      <WebRTCProvider userId={currentUser?.id}>
        <FinanceNotificationProvider>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            {/* Global incoming-call layer — one banner/ringtone/listener for every
                authenticated page (Synex Calling V2). Inside Router for useNavigate. */}
            <IncomingCallProvider>
            {/* Inside the Router so guards can navigate; wraps every route so that a
                Solution Access change re-renders the sidebar and route guards at once. */}
            <SessionProvider>
            <Suspense fallback={<PageLoader />}>
              <RouteErrorBoundary>
                <Routes>
                {/* 1. Public & Auth Routes (PublicRoutes covers landing/pricing etc) */}
                <Route path="/*" element={<PublicRoutes />} />

                {/* Auth entry points */}
                <Route path="/auth/login" element={<LoginPage />} />
                <Route path="/auth/signup" element={<SignupPage />} />
                <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/auth/waiting-for-activation" element={<WaitingForActivation />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />
                <Route path="/proposal/respond/:token" element={<ProposalRespondPage />} />
                <Route path="/capture/lead/:token" element={<PublicLeadCaptureLayout />} />
                <Route path="/capture/lead/:orgSlug/:token" element={<PublicLeadCaptureLayout />} />
                <Route path="/:orgSlug/capture/lead/:token" element={<PublicLeadCaptureLayout />} />

                {/* Organization-Specific Careers & Job Detail Routes */}
                <Route path="/:orgSlug/careers" element={<PublicCareersPage />} />
                <Route path="/:orgSlug/careers/:id" element={<PublicJobDetailPage />} />
                <Route path="/:orgSlug/jobs/:id" element={<PublicJobDetailPage />} />

                {/* Synex Connect — full-screen meeting route (mounted outside Workspace chrome) */}
                <Route
                  path="/connect/*"
                  element={<ConnectRoutes />}
                />


                {/* Synex Meet — PUBLIC guest meeting route (no auth, no Workspace chrome).
                    More specific than the "/*" PublicRoutes splat, so it takes precedence. */}
                <Route path="/meet/*" element={<MeetRoutes />} />

                {/* Synex Mail — PUBLIC Application Home Page (Google OAuth verification).
                    This is the recommended Application Home Page URL for the Google Cloud
                    OAuth Consent Screen: https://synexos.ai/mail */}
                <Route path="/mail" element={<MailLandingPage />} />

                {/* Synex Mail — PUBLIC legal pages (no auth, no Workspace chrome).
                    Required for Google OAuth production verification. */}
                <Route path="/mail/privacy" element={<MailPrivacyPolicy />} />
                <Route path="/mail/terms" element={<MailTermsOfService />} />
                <Route path="/mail/data-deletion" element={<MailDataDeletion />} />
                <Route path="/mail/security" element={<MailSecurity />} />

                {/* Client Portal Integration Namespace */}
                <Route
                  path="/client-portal/*"
                  element={
                    <ClientPortalAuthProvider>
                      <VendorAuthProvider>
                        <ClientPortalProfileProvider>
                          <ClientPortalRouter />
                        </ClientPortalProfileProvider>
                      </VendorAuthProvider>
                    </ClientPortalAuthProvider>
                  }
                />

                {/* Vendor Portal Integration Namespace */}
                <Route
                  path="/vendor-portal/*"
                  element={<VendorPortalRouter />}
                />

                {/* Authenticated SynexOS Home. Where a member with no assigned solutions
                    lands. Authenticated but NOT solution-gated, or the guards would bounce.
                    Renders inside the standard application shell, in homeMode. */}
                <Route
                  path="/home"
                  element={
                    <PrivateRoute>
                      <WorkspaceLayoutNew homeMode setIsAuthenticated={setIsAuthenticated} />
                    </PrivateRoute>
                  }
                >
                  <Route index element={<SynexosHome />} />
                </Route>
                {/* Legacy path kept so existing links/bookmarks keep working. */}
                <Route path="/no-access" element={<Navigate to="/home" replace />} />

                {/* 2. Workspace Protected Routes */}
                <Route
                  path="/workspace/*"
                  element={
                    <PrivateRoute solution="workspace">
                      <WorkspaceLayoutNew setIsAuthenticated={setIsAuthenticated} />
                    </PrivateRoute>
                  }
                >
                  <Route path="*" element={<WorkspaceRoutes />} />
                </Route>

                {/* 3. Commerce Protected Routes */}
                <Route
                  path="/commerce/*"
                  element={
                    <PrivateRoute>
                      <WorkspaceLayoutNew setIsAuthenticated={setIsAuthenticated} />
                    </PrivateRoute>
                  }
                >
                  <Route path="*" element={<CommerceRoutes />} />
                </Route>

                {/* 4. Finance Protected Routes */}
                <Route
                  path="/finance/*"
                  element={
                    <PrivateRoute>
                      <WorkspaceLayoutNew setIsAuthenticated={setIsAuthenticated} />
                    </PrivateRoute>
                  }
                >
                  <Route path="*" element={<FinanceRoutes />} />
                </Route>

                {/* 5. Inventory & Operations Protected Routes */}
                <Route
                  path="/operations/*"
                  element={
                    <PrivateRoute>
                      <WorkspaceLayoutNew setIsAuthenticated={setIsAuthenticated} />
                    </PrivateRoute>
                  }
                >
                  <Route path="*" element={<OpsRoutes />} />
                </Route>

                {/* 5b. Procurement — inside the global shell, gated on the procurement solution */}
                <Route
                  path="/solutions/operations/procurement/*"
                  element={
                    <PrivateRoute solution="procurement">
                      <WorkspaceLayoutNew setIsAuthenticated={setIsAuthenticated} />
                    </PrivateRoute>
                  }
                >
                  <Route path="*" element={<ProcurementRoutes />} />
                </Route>

                {/* 5c. Workforce — inside the global shell, gated on the workforce solution */}
                <Route
                  path="/solutions/workforce/*"
                  element={
                    <PrivateRoute solution="workforce">
                      <WorkspaceLayoutNew setIsAuthenticated={setIsAuthenticated} />
                    </PrivateRoute>
                  }
                >
                  <Route path="*" element={<WorkforceRoutes />} />
                </Route>


                {/* 7. Admin & Governance Protected Routes */}
                <Route
                  path="/admin/*"
                  element={
                    <PrivateRoute>
                      <WorkspaceLayoutNew setIsAuthenticated={setIsAuthenticated} />
                    </PrivateRoute>
                  }
                >
                  <Route path="*" element={<AdminRoutes />} />
                </Route>

                {/* 7c. Dedicated Settings & Hierarchical Team Management */}
                <Route
                  path="/settings/*"
                  element={
                    <PrivateRoute>
                      <WorkspaceLayoutNew setIsAuthenticated={setIsAuthenticated} />
                    </PrivateRoute>
                  }
                >
                  <Route path="*" element={<SettingsRoutes />} />
                </Route>

                {/* 7d. Dedicated User Profile (Permission-Aware Access) */}
                <Route
                  path="/profile/*"
                  element={
                    <PrivateRoute>
                      <WorkspaceLayoutNew setIsAuthenticated={setIsAuthenticated} />
                    </PrivateRoute>
                  }
                >
                  <Route path="*" element={<MyProfilePage />} />
                </Route>

                {/* 7b. Internal Operations Console — Synexos staff only.
                    Deliberately outside WorkspaceLayoutNew: this is not customer chrome.
                    Gated on platform_staff credentials inside InternalRoutes. */}
                <Route path="/internal/*" element={<InternalRoutes />} />

                {/* 8. Specialized Modules */}
                <Route
                  path="/email-campaigns/*"
                  element={
                    <PrivateRoute>
                      <WorkspaceLayoutNew setIsAuthenticated={setIsAuthenticated} />
                    </PrivateRoute>
                  }
                >
                  <Route index element={<EmailCampaigns />} />
                </Route>

                <Route
                  path="/workflows/*"
                  element={
                    <PrivateRoute>
                      <WorkspaceLayoutNew setIsAuthenticated={setIsAuthenticated} />
                    </PrivateRoute>
                  }
                >
                  <Route index element={<WorkflowBuilder />} />
                </Route>

                {/* Default Internal Redirects */}
                <Route path="/ib-finance/*" element={<Navigate to="/finance" replace />} />
                <Route path="/ib-workforce/*" element={<Navigate to="/operations" replace />} />

                {/* Organization-prefixed Public Lead Capture Link (/{org_slug}/{custom_slug}) */}
                <Route path="/:orgSlug/:customSlug" element={<PublicLeadCaptureLayout />} />

                {/* Legacy Redirects */}
                <Route path="/dashboard" element={<Navigate to="/workspace" replace />} />
                <Route path="/login" element={<Navigate to="/auth/login" replace />} />
              </Routes>
            </RouteErrorBoundary>
          </Suspense>
            {/* Persistent Chat/call host — outside <Routes> so navigating never unmounts an
                active call. Renders nothing unless the Chats route is open or a call/huddle
                is in progress, and only while authenticated (logout ends the call). */}
            <PersistentChatsHost isAuthenticated={isAuthenticated} />
            </SessionProvider>
            </IncomingCallProvider>
          </BrowserRouter>
        </FinanceNotificationProvider>
      </WebRTCProvider>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

export default App;
